import { UzButton, UzCombobox, UzDialog, UzSelect, UzSwitch } from '@utazzo/ui';
import React, { useMemo, useRef, useState } from 'react';

import { useCountries, useUploadDocument } from '../use-customers';

import type { CustomerDocumentKind } from '@utazzo/shared';

const MAX_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB (mirrors server schema)

const DOCUMENT_KIND_OPTIONS = [
  { value: 'PASSPORT', label: 'Passport' },
  { value: 'VISA', label: 'Visa' },
  { value: 'PAN', label: 'PAN card' },
  { value: 'AADHAAR', label: 'Aadhaar card' },
  { value: 'GST_CERTIFICATE', label: 'GST certificate' },
  { value: 'COMPANY_REGISTRATION', label: 'Company registration' },
  { value: 'OTHER_ID', label: 'Other identity document' },
  { value: 'INVOICE', label: 'Invoice' },
  { value: 'OTHER', label: 'Other' },
];

interface DocumentUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
}

const EMPTY_STATE = {
  file: null as File | null,
  documentKind: '' as CustomerDocumentKind | '',
  title: '',
  documentNumber: '',
  issuedCountryId: '',
  issueDate: '',
  expiryDate: '',
  isPrimary: false,
  fileError: '',
};

/**
 * Dialog for the two-step S3 document upload (ADR-0014).
 * The file size limit (25 MB) is validated client-side before calling init
 * to give instant feedback before any network round-trip.
 */
export const DocumentUploadDialog = ({
  open,
  onOpenChange,
  customerId,
}: DocumentUploadDialogProps): React.JSX.Element => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadDocument = useUploadDocument();
  const countriesQuery = useCountries();
  const pending = uploadDocument.isPending;

  const [form, setForm] = useState(EMPTY_STATE);

  const countryOptions = useMemo(
    () =>
      (countriesQuery.data ?? []).map((c) => ({
        value: c.id,
        label: `${c.name} (${c.isoCode.toUpperCase()})`,
      })),
    [countriesQuery.data],
  );

  const resetForm = () => {
    setForm(EMPTY_STATE);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    if (selected && selected.size > MAX_SIZE_BYTES) {
      setForm((prev) => ({
        ...prev,
        file: null,
        fileError: `File is too large (${(selected.size / 1024 / 1024).toFixed(1)} MB). Maximum is 25 MB.`,
      }));
      e.target.value = '';
      return;
    }
    setForm((prev) => ({ ...prev, file: selected, fileError: '' }));
  };

  const handleSubmit = async () => {
    if (!form.file) {
      setForm((prev) => ({ ...prev, fileError: 'Please select a file to upload.' }));
      return;
    }
    if (!form.documentKind) return;
    if (!form.title.trim()) return;

    await uploadDocument.mutateAsync({
      customerId,
      file: form.file,
      meta: {
        documentKind: form.documentKind as CustomerDocumentKind,
        title: form.title.trim(),
        isPrimary: form.isPrimary,
        documentNumber: form.documentNumber.trim() || null,
        issuedCountryId: form.issuedCountryId || null,
        issueDate: form.issueDate || null,
        expiryDate: form.expiryDate || null,
      },
    });
    resetForm();
    onOpenChange(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) resetForm();
    onOpenChange(isOpen);
  };

  return (
    <UzDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="Upload document"
      description="Select a file and fill in the document details. Files are uploaded directly to secure storage; the API server never receives the binary."
      size="xl"
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => handleOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton
            onClick={handleSubmit}
            loading={pending}
            disabled={!form.file || !form.documentKind || !form.title.trim()}
          >
            Upload
          </UzButton>
        </div>
      }
    >
      <div className="space-y-3">
        {/* File picker */}
        <div>
          <label htmlFor="doc-file-input" className="mb-1 block text-sm font-medium text-zinc-700">
            File{' '}
            <span className="text-destructive" aria-hidden>
              *
            </span>
          </label>
          <input
            id="doc-file-input"
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            onChange={handleFileChange}
            disabled={pending}
            className="block w-full text-sm text-zinc-700 file:mr-3 file:rounded file:border-0 file:bg-zinc-100 file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-zinc-200 disabled:opacity-50"
            aria-describedby={form.fileError ? 'doc-file-error' : undefined}
            aria-invalid={Boolean(form.fileError)}
          />
          {form.fileError && (
            <p id="doc-file-error" className="text-destructive mt-1 text-xs" role="alert">
              {form.fileError}
            </p>
          )}
          {form.file && !form.fileError && (
            <p className="mt-1 text-xs text-zinc-500">
              {form.file.name} ({(form.file.size / 1024).toFixed(0)} KB)
            </p>
          )}
        </div>

        {/* Document kind */}
        <UzSelect
          name="documentKind"
          label="Document type"
          required
          options={DOCUMENT_KIND_OPTIONS}
          value={form.documentKind || undefined}
          onValueChange={(v) =>
            setForm((prev) => ({ ...prev, documentKind: v as CustomerDocumentKind }))
          }
          disabled={pending}
        />

        {/* Title */}
        <div>
          <label htmlFor="doc-title" className="mb-1 block text-sm font-medium text-zinc-700">
            Title{' '}
            <span className="text-destructive" aria-hidden>
              *
            </span>
          </label>
          <input
            id="doc-title"
            type="text"
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
            placeholder="e.g. Priya Sharma - Passport"
            disabled={pending}
            required
            className="border-input placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>

        {/* Document number */}
        <div>
          <label htmlFor="doc-number" className="mb-1 block text-sm font-medium text-zinc-700">
            Document number
          </label>
          <input
            id="doc-number"
            type="text"
            value={form.documentNumber}
            onChange={(e) => setForm((prev) => ({ ...prev, documentNumber: e.target.value }))}
            placeholder="Optional"
            disabled={pending}
            className="border-input placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>

        {/* Issued country */}
        <UzCombobox
          name="issuedCountryId"
          label="Issued country"
          placeholder={countriesQuery.isLoading ? 'Loading…' : 'Select (optional)'}
          searchPlaceholder="Search countries…"
          options={countryOptions}
          value={form.issuedCountryId || undefined}
          onValueChange={(v) => setForm((prev) => ({ ...prev, issuedCountryId: v ?? '' }))}
          loading={countriesQuery.isLoading}
          emptyText="No country found"
          disabled={pending}
        />

        {/* Issue date / expiry date */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label
              htmlFor="doc-issue-date"
              className="mb-1 block text-sm font-medium text-zinc-700"
            >
              Issue date
            </label>
            <input
              id="doc-issue-date"
              type="date"
              value={form.issueDate}
              onChange={(e) => setForm((prev) => ({ ...prev, issueDate: e.target.value }))}
              disabled={pending}
              className="border-input focus-visible:ring-ring flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <div>
            <label
              htmlFor="doc-expiry-date"
              className="mb-1 block text-sm font-medium text-zinc-700"
            >
              Expiry date
            </label>
            <input
              id="doc-expiry-date"
              type="date"
              value={form.expiryDate}
              onChange={(e) => setForm((prev) => ({ ...prev, expiryDate: e.target.value }))}
              disabled={pending}
              className="border-input focus-visible:ring-ring flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
        </div>

        {/* Primary toggle */}
        <UzSwitch
          label="Primary document"
          description="Mark as the primary document of this kind for the customer."
          checked={form.isPrimary}
          onCheckedChange={(v) => setForm((prev) => ({ ...prev, isPrimary: v }))}
          disabled={pending}
        />
      </div>
    </UzDialog>
  );
};
